import { SubmapFacetValue, SubmapNode } from '../entities/submap-node.entity';
import { PATH_SEPARATOR, toSubmapSegments } from './group-by-submap.helper';

/**
 * HFBD-6 decision 6: how many rows the Submap tree may show when it opens.
 *
 * ⚠ A named constant on purpose — it is exactly the value a designer will want to move once. Roughly one
 * screen of filter panel.
 */
export const SUBMAP_ROW_BUDGET: number = 28;

/** How many values the tree asks the engine for. The engine exposes at most 100 and its own default is 10. */
export const SUBMAP_FACET_VALUE_COUNT: number = 100;

/** The query value set that names a node: its own segment and every segment above it, root first. */
export function toNodeSegments(node: SubmapNode): string[] {
    return [...node.ancestors, node.segment];
}

/** A node's identity inside one tree. Not a query value — the query names segments, never a joined path. */
export function toNodeKey(node: SubmapNode): string {
    return toNodeSegments(node).join(PATH_SEPARATOR);
}

/**
 * Rolls a `Dynamic.DocumentPath` facet response up into the folder tree the panel draws.
 *
 * ⚠ **Only the folders are levels.** The first segment of a path is the dossier and the last is the
 * filename, so both ends come off — the same {@link toSubmapSegments} rule the submap header rows above the
 * table follow, so the panel and the table can never disagree about what a folder is. The product owner
 * confirmed on 2026-08-27 that a dossier row and a filename row are not wanted.
 *
 * The engine returns **one bucket per distinct path**, never one per folder, so every folder and every count
 * here is computed in the browser. Two records really can share a path — the live capture found a bucket with
 * `Count: 2` — which is why a count sums `Count` rather than counting buckets.
 *
 * A folder's count is **descendant-inclusive**: the number of records in it and in every folder below it. That
 * is the number selecting it will show, because the query term matches a segment anywhere in a path.
 *
 * ⚠ A stuk in no folder contributes **nothing**: no row and no count anywhere. There is no *"Geen submap"*
 * value — it was offered and declined. A `Missing` bucket arrives unasked on a `CustomFacets` call, and it is
 * discarded here for the same reason.
 *
 * Order is **first appearance**, per level, not alphabetical — the same rule the submap header rows follow, so
 * the panel and the table agree about which folder comes first.
 */
export function toSubmapNodes(values: readonly SubmapFacetValue[]): SubmapNode[] {
    const roots: SubmapNode[] = [];
    const byKey: Map<string, SubmapNode> = new Map();

    for (const value of values) {
        const segments: string[] = toSubmapSegments(value.path, { dropDossierSegment: true });
        const ancestors: string[] = [];

        for (const segment of segments) {
            const key: string = [...ancestors, segment].join(PATH_SEPARATOR);
            let node: SubmapNode | undefined = byKey.get(key);

            if (!node) {
                node = { segment, ancestors: [...ancestors], count: 0, children: [] };
                byKey.set(key, node);
                const parent: SubmapNode | undefined =
                    ancestors.length === 0 ? undefined : byKey.get(ancestors.join(PATH_SEPARATOR));
                (parent ? parent.children : roots).push(node);
            }

            // Every folder on the path is credited, which is what makes the count descendant-inclusive.
            node.count += value.count;
            ancestors.push(segment);
        }
    }

    return roots;
}

/** Every node in the tree, level by level — the order the budget expands in. */
export function toBreadthFirstNodes(nodes: readonly SubmapNode[]): SubmapNode[] {
    const ordered: SubmapNode[] = [];
    let level: SubmapNode[] = [...nodes];

    while (level.length > 0) {
        ordered.push(...level);
        level = level.flatMap(node => node.children);
    }

    return ordered;
}

/**
 * HFBD-6 decision 6: which folders are open when the tree first draws, as a set of node keys.
 *
 * **Breadth-first, not depth-first.** It shows the widest view of *where things are* before it shows how deep
 * any one branch goes.
 *
 * The budget counts **visible rows**: the root folders always show, and opening a folder costs one row per
 * child. Expansion stops at the first folder whose children would not fit, and does **not** then look for a
 * smaller folder further along — a tree that skipped a wide folder to open a narrow one below it would read
 * as arbitrary. ⚠ A folder left closed still renders its chevron and its count, so nothing hides the
 * existence of a subtree.
 *
 * `forcedOpenKeys` are the ancestors of an active selection. They open **before** the budget is applied and
 * cost nothing against it: a selected folder that is invisible on load is worse than any budget setting.
 *
 * ⚠ The result is **recomputed, never persisted**. It survives neither a reload nor a navigation, and it does
 * not need to: the budget produces the same view every time, and a selection re-opens its own ancestors.
 */
export function expandToBudget(
    nodes: readonly SubmapNode[],
    forcedOpenKeys: ReadonlySet<string>,
    budget: number = SUBMAP_ROW_BUDGET
): ReadonlySet<string> {
    const open: Set<string> = new Set(forcedOpenKeys);
    let rows: number = nodes.length;

    for (const node of toBreadthFirstNodes(nodes)) {
        if (node.children.length === 0 || open.has(toNodeKey(node))) {
            continue;
        }

        if (rows + node.children.length > budget) {
            break;
        }

        open.add(toNodeKey(node));
        rows += node.children.length;
    }

    return open;
}

/**
 * The node the active filter names, or `null` when nothing does.
 *
 * Order is ignored, because it says nothing: the `f` URL parameter serialises in click order and `MUST` ANDs
 * its terms. ⚠ **Duplicates are not** — they are compared as a multiset rather than a set. A folder named the
 * same as its parent (`bestek` inside `bestek`) puts one segment in the chain twice, and a set would shrink it
 * to one and match the **parent** instead: the user would tick the child and watch the parent light up.
 *
 * A selection naming a folder that no longer exists matches no node and leaves the tree with nothing drawn as
 * selected — which is the honest result, and the case the empty state explains.
 */
export function toSelectedNodeKey(nodes: readonly SubmapNode[], activeSegments: readonly string[]): string | null {
    if (activeSegments.length === 0) {
        return null;
    }

    const active: string = canonicalSegments(activeSegments);
    const match: SubmapNode | undefined = toBreadthFirstNodes(nodes).find(
        node => canonicalSegments(toNodeSegments(node)) === active
    );

    return match ? toNodeKey(match) : null;
}

/**
 * A segment chain reduced to what the query actually means: which segments, and how many of each. Sorted so
 * two chains differing only in order compare equal, and joined with a separator no segment can hold — every
 * segment was split on it.
 */
function canonicalSegments(segments: readonly string[]): string {
    return [...segments].sort().join(PATH_SEPARATOR);
}

/** The keys of the folders above `nodeKey`, which an active selection forces open. */
export function toAncestorKeys(nodeKey: string): Set<string> {
    const segments: string[] = nodeKey.split(PATH_SEPARATOR);

    return new Set(segments.slice(0, -1).map((_, index) => segments.slice(0, index + 1).join(PATH_SEPARATOR)));
}

/**
 * HFBD-6 decision 7: whether the engine may have withheld folders.
 *
 * ⚠ **`HasMoreValues` is never read.** It reports `false` while truncating at the engine's 100-value ceiling —
 * a live probe asked for 100, got exactly 100, was told there were no more, and was demonstrably cut. So
 * truncation is detected by **count**: ask for `n`, and treat `returned === n` as *"there may be more"*.
 *
 * It over-reports exactly one case, a level holding precisely `n` values, which costs an affordance that
 * fetches nothing. That is the right direction to be wrong in.
 */
export function isTruncated(values: readonly SubmapFacetValue[], requestedValueCount: number): boolean {
    return values.length >= requestedValueCount;
}

/**
 * The `Value.Constraints.Prefix` that narrows one level to the folders starting with `term`.
 *
 * ⚠ The engine matches the prefix against the **whole** stored path, which starts with the dossier segment the
 * tree itself drops. So the dossier segment has to be put back, and it is read off the values already in hand
 * rather than passed in: on a page scoped to one dossier every path shares it. With no values there is nothing
 * to search and no box to type in, so `null` is the honest answer rather than a prefix that matches nothing.
 *
 * ⚠ Only the **negative** control ran live — a prefix matching nothing returned an empty list with http 200.
 * A real prefix has never been checked against the running engine. The panel therefore only offers this box
 * above the 100-value ceiling, where nothing else can reach a folder at all.
 */
export function toLevelPrefix(
    values: readonly SubmapFacetValue[],
    ancestors: readonly string[],
    term: string
): string | null {
    const dossierSegment: string | undefined = values[0]?.path.split(PATH_SEPARATOR)[0]?.trim();

    if (dossierSegment === undefined || dossierSegment.length === 0) {
        return null;
    }

    return [dossierSegment, ...ancestors, term].join(PATH_SEPARATOR);
}

/**
 * The tree's values with a per-level search result folded in.
 *
 * ⚠ A search result **adds** to the tree, it never replaces it. A prefixed call returns only the paths that
 * match, so swapping the values for it would collapse the whole tree to one branch — and the point of the box
 * is to reach a folder the panel had not rendered, not to hide the ones it had.
 *
 * Deduplicated on the path, keeping the value already in hand, so a folder cannot be counted twice.
 */
export function mergeSubmapValues(
    base: readonly SubmapFacetValue[],
    found: readonly SubmapFacetValue[]
): SubmapFacetValue[] {
    const paths: Set<string> = new Set(base.map(value => value.path));

    return [...base, ...found.filter(value => !paths.has(value.path))];
}
